import { createContext, useContext } from 'react'
import type { ControllerProps, FieldError, FieldPath, FieldValues } from 'react-hook-form'
import { Controller, FormProvider } from 'react-hook-form'

const Form = FormProvider

// ── Field context ────────────────────────────────────────────────────────────
// Stores the RHF FieldError produced by the nearest Controller so that
// <FormMessage /> can display it without needing to know the field name.

interface FormFieldContextValue {
  error: FieldError | undefined
}

const FormFieldContext = createContext<FormFieldContextValue>({ error: undefined })

/**
 * Wraps RHF `<Controller>` and injects the field's current validation error
 * into React context. This allows `<FormMessage />` to read the error without
 * any prop drilling or knowledge of the field name.
 */
const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: ControllerProps<TFieldValues, TName>,
) => {
  return (
    <Controller
      {...props}
      render={(renderProps) => (
        <FormFieldContext.Provider value={{ error: renderProps.fieldState.error }}>
          {props.render(renderProps)}
        </FormFieldContext.Provider>
      )}
    />
  )
}

// ── Layout primitives ─────────────────────────────────────────────────────────

const FormItem = ({ children }: { children: React.ReactNode }) => (
  <div className="space-y-2">{children}</div>
)

const FormLabel = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <label className={`block text-sm font-medium ${className ?? ''}`}>{children}</label>
)

const FormControl = ({ children }: { children: React.ReactNode }) => <div>{children}</div>

const FormDescription = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => <p className={`text-sm text-muted-foreground ${className ?? ''}`}>{children}</p>

// ── FormMessage ───────────────────────────────────────────────────────────────

/**
 * Displays the validation error for the enclosing `<FormField>`.
 * Falls back to explicit `children` when no RHF error is present.
 * Renders nothing (null) when there is no message to show.
 */
const FormMessage = ({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) => {
  const { error } = useContext(FormFieldContext)
  const body = error ? String(error.message ?? '') : children
  if (!body) return null
  return <p className={`text-sm font-medium text-destructive ${className ?? ''}`}>{body}</p>
}

export { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage }
